import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchExamAnswers, clearExamAnswers } from "../redux/slices/examHistorySlice";
import { FaCheckCircle, FaTimesCircle, FaArrowLeft } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AllExams/AllExams.css";
import { useDarkMode } from "../contexts/DarkModeContext";

function ExamAnswers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { testId } = useParams();
  const { answers, loading, error } = useSelector((state) => state.examHistory);
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    if (testId) dispatch(fetchExamAnswers(testId));
    return () => dispatch(clearExamAnswers());
  }, [dispatch, testId]);

  // حساب مجموع الدرجات
  const totalScore = useMemo(() => {
    if (!answers || !answers.length) return 0;
    return answers.reduce((sum, q) => sum + (q.is_correct ? q.question_degree : 0), 0);
  }, [answers]);
  const maxScore = useMemo(() => {
    if (!answers || !answers.length) return 0;
    return answers.reduce((sum, q) => sum + (q.question_degree || 0), 0);
  }, [answers]);

  return (
    <div className={`questions-section ${isDarkMode ? "dark-mode" : "light-mode"}`}>
      <ToastContainer />
      <div className="container">
        {/* Header */}
        <div className="lessons-breadcrumb">
          <h1 className="breadcrumb-title">إجابات الامتحان</h1>
          <div className="breadcrumb-links2">
            <span className="breadcrumb-link" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}><FaArrowLeft /> رجوع</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">إجابات الامتحان</span>
          </div>
        </div>
        {/* Score Summary */}
        <div className="exam-summary-box" style={{
          background: 'linear-gradient(90deg, #27ae60 0%, #00b894 100%)',
          color: '#fff',
          borderRadius: 20,
          padding: 24,
          margin: '30px 0',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          textAlign: 'center',
        }}>
          <h2 style={{ fontWeight: 800, fontSize: 28, marginBottom: 8 }}>درجتك النهائية</h2>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2 }}>{totalScore} / {maxScore}</div>
        </div>
        {/* Loading/Error */}
        {loading && <div className="text-center my-4">جاري التحميل...</div>}
        {error && <div className="text-center my-4" style={{ color: 'red' }}>{error}</div>}
        {/* Questions & Answers */}
        <div className="exam-answers-list">
          {answers && answers.length > 0 ? answers.map((q, idx) => (
            <div key={q.question_id} className="question-box" style={{
              border: '2px solid',
              borderColor: q.is_correct ? '#27ae60' : '#e74c3c',
              borderRadius: 18,
              marginBottom: 32,
              background: 'rgba(255,255,255,0.04)',
              boxShadow: q.is_correct ? '0 0 16px #27ae6044' : '0 0 16px #e74c3c44',
              position: 'relative',
            }}>
              <div style={{ position: 'absolute', top: 18, left: 18, fontSize: 28 }}>
                {q.is_correct ? <FaCheckCircle color="#27ae60" /> : <FaTimesCircle color="#e74c3c" />}
              </div>
              <h4 className="question-title" style={{ color: q.is_correct ? '#27ae60' : '#e74c3c' }}>
                {`السؤال ${idx + 1} (${q.question_degree} درجة)`}
              </h4>
              <p className="question-text">{q.question_title}</p>
              {q.question_image && (
                <img src={q.question_image} alt="سؤال" style={{ maxWidth: 300, borderRadius: 12, margin: '10px 0' }} />
              )}
              <div className="answers-list">
                {q.answers.map((ans) => {
                  // هل هذه هي إجابة الطالب؟
                  const isStudentAnswer = q.student_answer && ans.answer_id === q.student_answer.answer_id;
                  // هل هذه هي الإجابة الصحيحة؟
                  const isCorrect = ans.is_correct === 1;
                  return (
                    <div
                      key={ans.answer_id}
                      className={`answer-item`}
                      style={{
                        background: isCorrect
                          ? 'linear-gradient(90deg, #27ae60 0%, #00b894 100%)'
                          : isStudentAnswer
                          ? 'linear-gradient(90deg, #e74c3c 0%, #ff7675 100%)'
                          : 'rgba(255,255,255,0.08)',
                        color: isCorrect || isStudentAnswer ? '#fff' : '#ccc',
                        border: isCorrect ? '2px solid #27ae60' : isStudentAnswer ? '2px solid #e74c3c' : '1px solid #444',
                        marginBottom: 8,
                        borderRadius: 12,
                        boxShadow: isCorrect ? '0 0 8px #27ae6044' : isStudentAnswer ? '0 0 8px #e74c3c44' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        position: 'relative',
                      }}
                    >
                      <span className="answer-label" style={{ fontWeight: 700, fontSize: 18 }}>
                        {String.fromCharCode(65 + q.answers.indexOf(ans))}
                      </span>
                      <span className="answer-text">{ans.answer_text}</span>
                      {ans.answer_image && (
                        <img src={ans.answer_image} alt="إجابة" style={{ maxWidth: 80, borderRadius: 8, marginRight: 10 }} />
                      )}
                      {isCorrect && <span style={{ marginRight: 10, fontWeight: 700 }}>(الإجابة الصحيحة)</span>}
                      {isStudentAnswer && !isCorrect && <span style={{ marginRight: 10, fontWeight: 700 }}>(إجابتك)</span>}
                    </div>
                  );
                })}
              </div>
              {/* ملخص الطالب */}
              <div style={{ marginTop: 10, fontWeight: 700, color: q.is_correct ? '#27ae60' : '#e74c3c' }}>
                {q.is_correct ? `✔️ إجابتك صحيحة (+${q.question_degree})` : `❌ إجابتك خاطئة (0 من ${q.question_degree})`}
              </div>
            </div>
          )) : (
            <div className="text-center my-4">لا توجد بيانات للإجابات.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExamAnswers;